import mongoose from "mongoose";
import * as uuid from "uuid";
import { match } from "@mssi/pssp-shared";

export interface CustomerPreferenceEmailsCountry {
  _id: mongoose.Schema.Types.ObjectId;
  email?: string;
  country?: string;
}

export interface CustomerOpportunity {
  opportunityId: string;
  accountId: string;
  accountCategory: string;
  businessModel: string;
  /** note - needs to be converted from "true" or "false" possibly */
  global: boolean;
}

export interface CustomerCustomHeader {
  number?: number;
  value?: string;
}

export interface CustomerRelatedAccount {
  accountId?: string;
  accountName?: string;
  /** @deprecated */
  accountType?: string;
  accountCategory?: string;
  isReseller?: string;
  apolloOrgId?: string;
  resellerPrmId?: string;
  bpid?: string;
  /** @deprecated */
  salesMethod?: string;
  country?: string;
  geoRegion?: string;
}

export interface CustomerCustomHeaders {
  number: number;
  value: string;
}

export interface CustomerSetup {
  done?: boolean;
  close?: boolean; // sic - closed
  steps: Record<string, boolean>;
}

export interface CustomerFederatedLoginTenants {
  domain: string;
  identityProvider: string;
  externalTenantId: string;
}

export interface CustomerOrderDeploymentContacts {
  name: string,
  email: string,
  region: string,
  country: string,
}

export interface CustomerMasterAgreement {
  masterAgreementNumber: string;
  businessModel: string;
  businessModelType: string;
  startDate: Date;
  endDate: Date;
}

export interface CustomerAccountInfo {
  _id: mongoose.Schema.Types.ObjectId;
  accountMemberId?: string,
  name: string;
  email: string;
  role: string;
  position?: number;
  highlight: boolean;
  bio?: string;
}
export interface Customer {
  _id: mongoose.Schema.Types.ObjectId;
  id: string;
  /** @deprecated but still used by 2 customers in prod */
  name?: string;
  accountId?: string;
  itsmCustomerId?: string;
  itsmCustomerName?: string;
  customerOpportunityGroupId?: string;
  customerOpportunityGroupName?: string;
  customerOpportunityGroupFullyQualifiedName?: string;
  leadEndCustomerAccountId?: string;
  /** blank = legacy */
  accountCategory?: string;
  businessModel?: string[];
  primaryContactName?: string;
  primaryContactEmail?: string;
  featureCapabilities: string[];
  toolsEnablerPermissions: string[];
  explorePermissions: string[];
  ordersPermissions?: string[];
  serviceCatalogPermissions?: string[];
  hardwareBreakFixPermissions?: string[];
  softwareSupportPermissions?: string[];
  serviceRequestPermissions?: string[];
  /** @deprecated - part of email config */
  preferenceEmails: string[];
  isCBA: boolean;
  maximumOrderQuantity?: number;
  newCustomerSetupContractId?: string;
  newCustomerSetupSteps: string[];
  /** @deprecated - part of ordersPermissions */
  allowHomeAddress: boolean;
  createdDate?: string;
  /** @deprecated - part of email config */
  preferenceEmailsCountry: CustomerPreferenceEmailsCountry[];
  emailRestrictions?: string;
  /** @deprecated - part of email config */
  supportEmail?: string;
  /** @deprecated - part of email config */
  hardwareSupportEmail?: string;
  /** @deprecated - part of ordersPermissions */
  priorityRequest?: boolean;
  /** @deprecated - part of ordersPermissions */
  updateOrderStatusManually?: boolean;
  isReseller?: boolean;
  /** @deprecated - use accountCategory now */
  accountType?: string;
  apolloOrgId?: string;
  resellerPrmId?: string;
  bpid?: string;
  geoRegion?: string;
  country?: string;
  relatedAccounts?: CustomerRelatedAccount[];
  customHeaders?: CustomerCustomHeader[];
  setup?: CustomerSetup;
  federatedLoginRequired?: boolean;
  federatedLoginTenants?: CustomerFederatedLoginTenants[];
  orderDeploymentContacts?: CustomerOrderDeploymentContacts[];
  masterAgreements?: CustomerMasterAgreement[];
  accountInfo?: CustomerAccountInfo[];
  hasDevices?: boolean;
  hasDevicesInUse?: boolean;
  hasDevicesForServiceRequest?: boolean;
  hasOrders?: boolean;
  hasCases?: boolean;
  hasServiceRequests?: boolean;
  /** @deprecated - replaced with relatedAccounts (again) */
  opportunities?: CustomerOpportunity[];
}

export const CustomerMasterAgreementSchema = new mongoose.Schema<CustomerMasterAgreement & { _id: false }>({
  _id: false,
  businessModel: String,
  masterAgreementNumber: String,
  businessModelType: String,
  endDate: Date,
  startDate: Date,
});

export const CustomerSchema = new mongoose.Schema<Customer>({
  id: { type: String, index: true, default: () => uuid.v1(), unique: true },
  name: String,
  accountId: String,
  itsmCustomerId: { type: String, index: true },
  itsmCustomerName: { type: String, index: true },
  customerOpportunityGroupId: { type: String, index: true },
  customerOpportunityGroupName: String,
  leadEndCustomerAccountId: String,
  customerOpportunityGroupFullyQualifiedName: String,
  accountCategory: String,
  businessModel: [String],
  primaryContactName: String,
  primaryContactEmail: String,
  featureCapabilities: [String],
  toolsEnablerPermissions: [String],
  explorePermissions: [String],
  ordersPermissions: [String],
  serviceCatalogPermissions: [String],
  hardwareBreakFixPermissions: [String],
  softwareSupportPermissions: [String],
  serviceRequestPermissions: [String],
  preferenceEmails: [String],
  isCBA: { type: Boolean, default: () => false },
  maximumOrderQuantity: Number,
  newCustomerSetupContractId: String,
  newCustomerSetupSteps: [String],
  allowHomeAddress: { type: Boolean, default: () => false },
  createdDate: String,
  preferenceEmailsCountry: [{ _id: false, email: String, country: String }],
  emailRestrictions: String,
  supportEmail: String,
  hardwareSupportEmail: String,
  priorityRequest: Boolean,
  updateOrderStatusManually: Boolean,
  isReseller: Boolean,
  accountType: String,
  apolloOrgId: String,
  resellerPrmId: String,
  bpid: String,
  geoRegion: String,
  country: String,
  relatedAccounts: [{
    _id: false,
    accountId: String,
    accountName: String,
    accountType: String,
    accountCategory: String,
    isReseller: String,
    apolloOrgId: String,
    resellerPrmId: String,
    bpid: String,
    salesMethod: String,
    country: String,
    geoRegion: String,
  }],
  customHeaders: [{
    _id: false,
    number: Number,
    value: String,
  }],
  setup: {
    _id: false,
    done: Boolean,
    steps: mongoose.Schema.Types.Mixed,
    close: Boolean,
  },
  federatedLoginRequired: Boolean,
  federatedLoginTenants: [{
    _id: false,
    domain: String,
    identityProvider: String,
    externalTenantId: String,
  }],
  orderDeploymentContacts: [{
    _id: false,
    name: String,
    email: String,
    region: String,
    country: String,
  }],
  masterAgreements: [CustomerMasterAgreementSchema],
  accountInfo: [{
    _id: false,
    accountMemberId: { type: String },
    name: { type: String, required: true },
    email: { type: String, required: true },
    role: { type: String, required: true },
    position: { type: Number, default: 0 },
    highlight: { type: Boolean, default: false },
    bio: String,
  }],
  hasDevices: { type: Boolean, default: false },
  hasDevicesInUse: { type: Boolean, default: false },
  hasDevicesForServiceRequest: { type: Boolean, default: false },
  hasOrders: { type: Boolean, default: false },
  hasCases: { type: Boolean, default: false },
  hasServiceRequests: { type: Boolean, default: false },
  opportunities: [{
    _id: false,
    opportunityId: { type: String, index: true },
    accountId: { type: String, index: true },
    accountCategory: String,
    businessModel: String,
    /** note - needs to be converted from "true" or "false" possibly */
    global: Boolean,
  }],
});

export const props: (keyof Customer)[] = [
  "id",
  "name",
  "itsmCustomerName",
  "itsmCustomerId",
  "accountId",
  "primaryContactName",
  "primaryContactEmail",
  "accountInfo",
];

export const searchProps: (keyof Customer)[] = [
  "id",
  "name",
  "itsmCustomerName",
  "itsmCustomerId",
  "accountId",
  "primaryContactName",
  "primaryContactEmail",
  "accountInfo",
];
 
export function matches(a: Customer, b: Customer): boolean {
  return match(a, b, props);
}

export const customerUtils = {
  props,
  searchProps,
  matches,
};
